import * as React from 'react'
import {
  GridList,
  GridListItem,
  Button as AriaButton,
  DropIndicator,
  type GridListProps,
  type Key,
} from 'react-aria-components'
import { useDragAndDrop } from 'react-aria-components'
import { CaretUp, CaretDown } from '@phosphor-icons/react'
// Drag handle is the one lucide exception in fb-ui: Phosphor's DotsSixVertical
// is a thin dot-grid that's too faint to read as a grab affordance; lucide's
// stroked GripVertical is legible. (Like the ellipsis/Target exceptions.)
import { GripVertical } from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * SortableList — an accessible, reorderable list of rows: drag a row by its
 * handle to a new position, or nudge it with the up/down buttons. Built on
 * react-aria's `GridList` + `useDragAndDrop`, so keyboard reordering (focus the
 * handle → Enter to lift → arrows → Enter to drop) and screen-reader
 * announcements come for free — no drag-and-drop dependency.
 *
 * It's the shared primitive under any "arrange these rows" surface — the
 * Columns Panel's selected-column list, an "Organize stages" sequence editor,
 * a saved-view ordering, etc. The list owns *order* only; each row's leading
 * content (a checkbox, a color dot) and trailing actions (remove, a ⋯ menu,
 * edit/delete, a metadata label) are yours to slot in.
 *
 *   const [cols, setCols] = React.useState(initial)
 *   <SortableList
 *     items={cols}
 *     getItemId={(c) => c.id}
 *     onReorder={setCols}
 *     aria-label="Selected columns"
 *   >
 *     {(col, ctx) => (
 *       <SortableListItem
 *         textValue={col.label}
 *         onMoveUp={ctx.moveUp}
 *         onMoveDown={ctx.moveDown}
 *         isFirst={ctx.isFirst}
 *         isLast={ctx.isLast}
 *         actions={<SortableRowButton aria-label="Remove"><MinusCircle /></SortableRowButton>}
 *       >
 *         {col.label}
 *       </SortableListItem>
 *     )}
 *   </SortableList>
 *
 * State is controlled: `items` is the current order and `onReorder` is called
 * with the next order after any drag or nudge. The list never mutates — the
 * consumer holds the array.
 */

/** Per-row info handed to the `SortableList` render function. */
export interface SortableItemContext {
  /** Zero-based position of this row in the current order. */
  index: number
  /** Total number of rows. */
  count: number
  /** `true` when this is the first row (nothing above it). */
  isFirst: boolean
  /** `true` when this is the last row (nothing below it). */
  isLast: boolean
  /** Move this row one step toward the top; no-op when already first. */
  moveUp: () => void
  /** Move this row one step toward the bottom; no-op when already last. */
  moveDown: () => void
}

/** Move the item at `from` to `to`, returning a new array (or the same one if out of bounds). */
function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return items.slice()
  const next = items.slice()
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export interface SortableListProps<T>
  extends Pick<GridListProps<object>, 'renderEmptyState'> {
  /** Rows, in current display order. */
  items: T[]
  /** Stable unique id for a row — the drag/reorder key. */
  getItemId: (item: T) => string
  /** Called with the next order after a drag or up/down nudge. */
  onReorder: (nextItems: T[]) => void
  /** Render one row — return a `SortableListItem`. Receives per-row position + move helpers. */
  children: (
    item: T,
    ctx: SortableItemContext
  ) => React.ReactElement<SortableListItemProps>
  /** Accessible name for the list (required — it's a reorderable grid). */
  'aria-label': string
  /** Classes on the list container. */
  className?: string
}

export function SortableList<T>({
  items,
  getItemId,
  onReorder,
  children,
  className,
  renderEmptyState,
  'aria-label': ariaLabel,
}: SortableListProps<T>) {
  const { dragAndDropHooks } = useDragAndDrop({
    // Every draggable row needs drag data; the id round-trips as plain text.
    getItems: (keys) =>
      [...keys].map((key) => ({ 'text/plain': String(key) })),
    // Only reordering within this list — render a brand drop line, no copy cursor.
    renderDropIndicator: (target) => (
      <DropIndicator
        target={target}
        className={cn(
          'block h-0.5 rounded-full outline-none',
          'data-[drop-target]:bg-fbui-basil-600'
        )}
      />
    ),
    onReorder(e) {
      const draggedKeys = new Set([...e.keys].map(String))
      const targetKey = String(e.target.key)
      // Pull the dragged rows out, then splice them back in at the target edge.
      const kept = items.filter((it) => !draggedKeys.has(getItemId(it)))
      const moved = items.filter((it) => draggedKeys.has(getItemId(it)))
      const targetIdx = kept.findIndex((it) => getItemId(it) === targetKey)
      if (targetIdx === -1) return
      const insertAt =
        e.target.dropPosition === 'after' ? targetIdx + 1 : targetIdx
      onReorder([
        ...kept.slice(0, insertAt),
        ...moved,
        ...kept.slice(insertAt),
      ])
    },
  })

  const count = items.length

  return (
    <GridList
      aria-label={ariaLabel}
      // Reorder-only: rows carry their own actions, so no row selection.
      selectionMode="none"
      dragAndDropHooks={dragAndDropHooks}
      renderEmptyState={renderEmptyState}
      className={cn('flex flex-col gap-2 outline-none', className)}
    >
      {items.map((item, index) => {
        const id = getItemId(item)
        const ctx: SortableItemContext = {
          index,
          count,
          isFirst: index === 0,
          isLast: index === count - 1,
          moveUp: () => onReorder(moveItem(items, index, index - 1)),
          moveDown: () => onReorder(moveItem(items, index, index + 1)),
        }
        // Inject the collection key/id so the consumer's render fn never has to.
        return React.cloneElement(children(item, ctx), { key: id, id })
      })}
    </GridList>
  )
}
SortableList.displayName = 'SortableList'

/** Shared trailing-action button for a row (remove, ⋯ menu, edit, delete). */
export interface SortableRowButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Red destructive treatment (e.g. a delete action). */
  destructive?: boolean
}

export const SortableRowButton = React.forwardRef<
  HTMLButtonElement,
  SortableRowButtonProps
>(({ className, destructive, type, ...props }, ref) => (
  <button
    ref={ref}
    type={type ?? 'button'}
    className={cn(
      'inline-flex size-7 shrink-0 items-center justify-center rounded-md outline-none transition-colors',
      'text-fbui-gray-500 enabled:hover:bg-fbui-basil-100 enabled:hover:text-fbui-gray-700',
      'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-default',
      'disabled:cursor-not-allowed disabled:text-fbui-text-disabled disabled:hover:bg-transparent',
      '[&>svg]:size-4',
      destructive &&
        'enabled:hover:bg-fbui-red-50 enabled:hover:text-fbui-red-700',
      className
    )}
    {...props}
  />
))
SortableRowButton.displayName = 'SortableRowButton'

export interface SortableListItemProps {
  /**
   * Collection key — injected by `SortableList` from `getItemId`, so you don't
   * normally set it. (Present on the type for standalone/advanced use.)
   */
  id?: Key
  /**
   * Plain-text label for typeahead + accessibility. Required when `children`
   * isn't a plain string (e.g. a label with a leading dot or badge).
   */
  textValue?: string
  /** Leading content before the label — a checkbox, a color dot, an icon. */
  leading?: React.ReactNode
  /** The row label / main content. */
  children: React.ReactNode
  /** Trailing action controls (remove, ⋯ menu, edit/delete) — use `SortableRowButton`. */
  actions?: React.ReactNode
  /**
   * Nudge-up handler. Provide (with `onMoveDown`) to render the up/down reorder
   * buttons alongside drag — an explicit, always-reachable reorder affordance.
   */
  onMoveUp?: () => void
  /** Nudge-down handler. See `onMoveUp`. */
  onMoveDown?: () => void
  /** First row — the up button is present but inert (keeps the row's controls aligned). */
  isFirst?: boolean
  /** Last row — the down button is present but inert. */
  isLast?: boolean
  /** Classes on the row. */
  className?: string
}

/**
 * One row in a `SortableList`: a drag handle, an optional `leading` slot, the
 * label, optional up/down reorder buttons, and a trailing `actions` slot. The
 * sage row fill + handle match the Figma "reorder row" spec; the row dims while
 * dragging.
 */
export function SortableListItem({
  id,
  textValue,
  leading,
  children,
  actions,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  className,
}: SortableListItemProps) {
  const showReorderButtons = onMoveUp != null && onMoveDown != null
  return (
    <GridListItem
      id={id}
      textValue={textValue}
      className={cn(
        'group flex items-center gap-2 rounded-lg bg-fbui-basil-50 py-1.5 pl-2 pr-1.5 outline-none',
        'text-sm text-fbui-gray-900',
        'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-brand',
        'data-[dragging]:opacity-50',
        className
      )}
    >
      <AriaButton
        slot="drag"
        // Include the row's label so a screen reader tabbing among handles can
        // tell rows apart, rather than hearing "Drag to reorder" N times.
        aria-label={textValue ? `Drag ${textValue} to reorder` : 'Drag to reorder'}
        className={cn(
          'inline-flex size-7 shrink-0 cursor-grab items-center justify-center rounded-md text-fbui-gray-500 outline-none transition-colors',
          'hover:text-fbui-gray-700 data-[dragging]:cursor-grabbing',
          'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-default'
        )}
      >
        <GripVertical aria-hidden="true" className="size-4" />
      </AriaButton>

      {leading != null && <span className="flex shrink-0 items-center">{leading}</span>}

      <span className="min-w-0 flex-1 truncate">{children}</span>

      {showReorderButtons && (
        <>
          <SortableRowButton
            aria-label="Move up"
            onClick={onMoveUp}
            // First row: keep the slot for alignment, but there's nowhere up to go.
            className={cn(isFirst && 'invisible')}
            disabled={isFirst}
          >
            <CaretUp aria-hidden="true" weight="bold" />
          </SortableRowButton>
          <SortableRowButton
            aria-label="Move down"
            onClick={onMoveDown}
            className={cn(isLast && 'invisible')}
            disabled={isLast}
          >
            <CaretDown aria-hidden="true" weight="bold" />
          </SortableRowButton>
        </>
      )}

      {actions}
    </GridListItem>
  )
}
SortableListItem.displayName = 'SortableListItem'
